import { useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient, apiErrorMessage } from '@/lib/api'
import type { SessionResponse } from '@/lib/types'
import { useAuth } from '@/stores/authStore'

export default function LoginPage() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const navigate = useNavigate()
  const location = useLocation()
  const qc = useQueryClient()
  const { setSession } = useAuth()
  const from = (location.state as { from?: string } | null)?.from ?? '/dashboard'

  const mut = useMutation({
    mutationFn: async (vars: { email: string; password: string }) => {
      const r = await apiClient.post<SessionResponse & { token: string }>('/api/v1/auth/login', vars)
      return r.data
    },
    onSuccess: (data) => {
      setSession(data.user, data.workspace)
      qc.setQueryData(['session'], { user: data.user, workspace: data.workspace })
      navigate(from, { replace: true })
    },
  })

  return (
    <div className="w-full max-w-sm rounded border bg-white p-6 shadow-sm">
      <h1 className="text-xl font-semibold mb-4">Sign in</h1>
      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault()
          mut.mutate({ email, password })
        }}
      >
        <label className="block text-sm">
          Email
          <input
            type="email"
            required
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="mt-1 w-full rounded border px-2 py-1.5 text-sm"
          />
        </label>
        <label className="block text-sm">
          Password
          <input
            type="password"
            required
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="mt-1 w-full rounded border px-2 py-1.5 text-sm"
          />
        </label>
        {mut.error && (
          <p className="text-sm text-red-600">{apiErrorMessage(mut.error)}</p>
        )}
        <button
          type="submit"
          disabled={mut.isPending}
          className="w-full rounded bg-neutral-900 text-white text-sm py-2 disabled:opacity-50"
        >
          {mut.isPending ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
      <p className="mt-4 text-sm text-neutral-600">
        No account?{' '}
        <Link to="/register" className="text-neutral-900 underline">
          Create one
        </Link>
      </p>
    </div>
  )
}
